<template>
  <GoogleMaps :preload="preload" @reachViewport="initMapService" class="interactive-map">
    <div ref="mapWidget" :class="{
      'interactive-map__widget': true,
      'interactive-map__widget--hidden': !widgetShown,
      }">
      
      <ToggleWidget v-if="widgetShown" @toggle="toggleWidget" value="Zwiń" icon="cross" />
      
      <div class="interactive-map__widget--header">
        <h3>Znajdź miejsce na idealne</h3>
        <h2>Wakacje</h2>
        <hr>
        <ToggleWidget v-if="!widgetShown" @toggle="toggleWidget" value="Rozwiń" icon="v" />
      </div>

      <div v-if="widgetShown" class="interactive-map__widget--body">
        <div class="interactive-map__widget--description">
          Wybierz miasto wylotu i zobacz na mapie szczegóły wspaniałych wysp, na których możesz być już w 3h!
        </div>

        <div class="interactive-map__widget--select">
          <span 
            class="interactive-map__widget--select-title" 
            @click="toggleAirportSelect">
            {{ selectedAirport || setDefaultSelectTitle() }}
          </span>
          
          <div class="interactive-map__widget--select-dropdown" v-if="airportSelectShown">
            <button @click="toggleAirportSelect" class="interactive-map__widget--select-close" type="button" />
            
            <div class="interactive-map__widget--select-group">
              <div v-for="(airport, code, index) in airports" :key="index" class="interactive-map__widget--select-option">
                <input :id="`${id}-${index}`" @change="selectAirportsOnMap" v-model="selectedAirport" :value="airport" :data-code="code"
                  name="airport" type="radio">
                <label :for="`${id}-${index}`">{{ airport }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <div class="interactive-map__widget--slider" ref="slider">
        <div class="swiper-wrapper">
          <div v-for="(region, index) in regions" :key="index" class="swiper-slide region-item"
            :data-regionId="index"
            :data-lat="region.Latitude"
            :data-lng="region.Longitude"
            :style="{
              background: `url(${region.MiniatureUrl})`
            }">
            <span>{{ region.Name }}</span>
          </div>
        </div>
        <div class="swiper-button-prev" />
        <div class="swiper-button-next" />
      </div>
    </div>

    <div ref="mapWrapper" class="interactive-map__wrapper" />
  </GoogleMaps>
</template>

<script>
import Swiper from 'swiper'
import Vue from 'vue'
import Component from 'vue-class-component'

/**
 * @todo TEMPORARY! In branch IG-1211 now we have nice Swiper component.
 * This will be replaced soon.
 */
import 'swiper/dist/css/swiper.css'

import InfoWindow from './integration/InfoWindow'

import ToggleWidget from '@/components/googleMap/buttons/ToggleWidget'
import InteractiveMapMixin from '@/components/googleMap/InteractiveMapMixin'
import GoogleMaps from '@/components/GoogleMaps'
import GoogleMapsMixin from '@/components/GoogleMapsMixin'
import { API } from '@/modules/api'
import { mapMarkerIcon } from '@/modules/globals'
import GoogleMapsService from '@/services/GoogleMapsService'
import Observer from '@/utils/observer'

@Component({
  mixins: [
    GoogleMapsMixin,
    InteractiveMapMixin,
  ],
  components: {
    GoogleMaps,
    ToggleWidget,
  },
  props: {
    preload: {
      type: Object,
      required: false,
    },
  },
  watch: {
    selectedAirport (val) {
      this.$emit('input', val)
    },
  },
})
export default class InteractiveMap extends Vue {
  mounted () {
    if (!this.preload) this.initMapService()
  }

  initMapService () {
    new GoogleMapsService().init()
      .then(() => new API().getJsonForInteractiveMap())
      .then(json => this.setMapData(json))
      .then(() => this.setMapRootElements())
      .then(() => this.init()) 
  }

  setMapRootElements () {
    this.mapWrapper = this.$refs.mapWrapper
    this.mapWidget = this.$refs.mapWidget
    this.slider = this.$refs.slider    
  }

  mapSettings () {
    return {
      zoom: parseInt(this.mapData.zoom),
      center: this.getCenterCoords(this.mapData),
      styles: this.loadCustomMapStyles(this.mapData.styles),
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.TOP_RIGHT,
      },
    }
  }
  
  watchForDuplicatedSlides (target) {
    const config = { childList: true }
    const type = 'childList'

    return new Observer(target, config, type).observe(mutation => mutation)
  }

  setSlidesPerView (count) {
    return count >= 3 ? 3 : count
  }

  setSlidesLoop (regionsCount, min) {
    return regionsCount > min ? true : false
  }

  mountRegionsSlider (root) {
    this.swiper = new Swiper(root, {
      slidesPerView: this.setSlidesPerView(this.regions.length),
      loop: this.setSlidesLoop(this.regions.length, 3),
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      breakpoints: {
        480: {
          slidesPerView: 2,
          loop: this.setSlidesLoop(this.regions.length, 2),
        },
      },
    })
  }

  showRegionOnMap (root) {
    root.querySelectorAll('.region-item').forEach(element => {
      element.addEventListener('click', e => {
        const regionID = e.target.dataset.regionid
        google.maps.event.trigger(this.regionMarkers[regionID], 'click')
      })
    })
  }

  selectAirportsOnMap (e) {
    const code = e.target.dataset.code
    const matrix = this.mapData.matrix

    for (const item in matrix) {
      const cityCode = matrix[item].CityCode
      const destinations = matrix[item].Destinations

      if (cityCode === code && destinations.length) {
        return this.selectRegionFromDropdown(destinations)
      } else {
        this.infoWindow.close()
        this.setMapMarkersIcon(this.destinationAirportsMarkers, mapMarkerIcon.inactive)
      }
    }

    return this.setMapMarkersIcon(this.regionMarkers, mapMarkerIcon.regionInactive)
  }

  /**
   * 
   * @param {Array} destinations with destination regions
   */
  selectRegionFromDropdown (destinations) {
    const airportsToSelect = []
    for (let i = 0; i < this.destinationAirports.length; i++) {
      const dest = this.destinationAirports[i]

      for (let j = 0; j < destinations.length; j++) {
        if (dest.City === destinations[j].City) airportsToSelect.push(dest)
      }

      // First we need to deselect all region markers
      this.regionMarkers.map(marker => marker.setIcon(mapMarkerIcon.regionInactive))

      // And now we can set all filtered markers as active
      const markersToActivate = this.markersToActivate(this.destinationAirportsMarkers, airportsToSelect, mapMarkerIcon.inactive)
      this.setMapMarkersIcon(markersToActivate, mapMarkerIcon.active)
    }
  }

  setupRegionsOnMap (IW, mapData) {
    this.regions.forEach(region => {
      const position = this.setNewMarkerPosition(region.Latitude, region.Longitude)
      const content = IW.getContentTemplate(region)
      
      const marker = this.getMarker(position, this.googleMap)
      this.regionMarkers.push(marker)

      marker.addListener('click', () => {
        this.infoWindow.setOptions({
          content,
          maxWidth: IW.setMaxWidth(),
          pixelOffset: IW.setPixelOffset(),
        })
        
        this.infoWindow.open(this.googleMap, marker)
        IW.styleInfoWindow()

        const markersToActivate = this.markersToActivate(this.regionMarkers, [ marker ], mapMarkerIcon.regionInactive)
        this.setMapMarkersIcon(markersToActivate, mapMarkerIcon.regionActive)
        this.setMapMarkersIcon(this.destinationAirportsMarkers, mapMarkerIcon.inactive)
      })

      google.maps.event.addListener(this.googleMap, 'click', () => {
        this.infoWindow.close()
        this.setMapMarkersIcon(this.regionMarkers)
        this.setMapMarkersIcon(this.destinationAirportsMarkers, mapMarkerIcon.defaults)
        this.setDefaultMapPosition(this.googleMap, mapData)
        this.setDefaultSelectTitle()

        this.airportSelectShown = false
      })
    })
  }

  setupDestinationAirportsOnMap () {
    this.destinationAirports.forEach(airport => {
      const position = this.setNewMarkerPosition(airport.Latitude, airport.Longitude)
      const marker = this.getMarker(position, this.googleMap, mapMarkerIcon.defaults)
      this.destinationAirportsMarkers.push(marker)
    })
  }

  setupRegionsSlider () {
    return Promise.resolve()
      .then(() => this.watchForDuplicatedSlides(this.slider))
      .then(() => this.mountRegionsSlider(this.slider))
      .then(() => this.showRegionOnMap(this.slider))
  }

  init () {
    const IW = new InfoWindow()

    this.regions = this.mapData.regions
    this.airports = this.mapData.airports
    this.destinationAirports = this.mapData.destinationAirports
    this.googleMap = this.setNewMap(this.mapWrapper, this.mapSettings())
    this.infoWindow = IW.getInfoWindow()

    this.setupRegionsOnMap(IW, this.mapData)
    this.setupDestinationAirportsOnMap()
    this.setupRegionsSlider()
  }
}

</script>

<style lang="scss">
.interactive-map {
  padding: 0 10px;

  @media screen and (min-width: 1350px) {
    padding: 0;
  }
}
.interactive-map__wrapper {
  height: 400px;

  @media screen and (min-width: 768px) {
    height: 500px;
  }
  @media screen and (min-width: 1350px) {
    height: 600px;
  }
}

.interactive-map__widget {
  width: 100%;
  z-index: 1;
  background-color: #fff;
  color: #005ca9;

  @media screen and (min-width: 768px) {
    position: absolute;
    width: 420px;
    top: 40px;
    left: 40px;
  }
  @media screen and (min-width: 1350px) {
    top: 60px;
    left: 95px;
  }

  &--slider {
    overflow: hidden;

    .region-item {
      display: flex !important;
      justify-content: center;
      align-items: center;
      text-align: center;
      text-transform: uppercase;
      background-size: cover !important;
      background-position: center !important;
      color: #fff;
      cursor: pointer;
      outline: none;
      font-family: "Gotham Book";
      height: 100px;

      &.disabled {
        opacity: .7;
      }

      span {
        pointer-events: none;
      }
    }
    .swiper-button-prev,
    .swiper-button-next {
      display: none;
      margin-top: 0;
      top: initial;
      bottom: 36px;
      width: 15px;
      height: 25px;
      background-size: 15px 25px;

      @media screen and (min-width: 768px) {
        display: block;
      }
    }
    .swiper-button-prev {
      left: -30px;
    }
    .swiper-button-next {
      right: -30px;
    }
  }

  &--header {
    padding: 20px;
    padding-bottom: 0;

    @media screen and (min-width: 1350px) {
      padding: 45px;
      padding-bottom: 0;
    }

    hr {
      display: inline-block;
      vertical-align: middle;
      width: 40px;
      margin: 0;
      border: 0;
      border-bottom: 6px solid #005ca9;
    }
    h2 {
      font-size: 32px;
      font-family: "Gotham";
      font-stretch: normal;
      margin-top: 3px;
      text-transform: uppercase;

      @media screen and (min-width: 768px) {
        font-size: 36px;
      }
    }
    h3 {
      font-size: 14px;
      font-family: "Georgia";
      font-style: italic;
      
      @media screen and (min-width: 768px) {
        font-size: 16px;
      }
    }
  }

  &--body {
    padding: 20px;
    padding-top: 0;

    @media screen and (min-width: 1350px) {
      padding: 45px;
      padding-top: 0;
    }
  }

  &--description {
    font-family: 'Gotham Book';
    font-size: 12px;
    margin-top: 15px;
  }

  &--select-title {
    font-family: "Gotham Book";
    font-weight: normal;
    font-style: normal;
    overflow: hidden;
    z-index: 2;
    font-size: 14px;
    border-bottom: 1px solid #055A9D;
    cursor: pointer;
    width: 270px;
    padding: 8px 2px 8px 8px;
    display: inline-block;
    position: relative;
    margin: 25px 0 10px;

    &::before {
      content: "\EA1C";
      font-family: "icons";
      font-style: normal;
      font-variant: normal;
      font-weight: normal;
      line-height: 1;
      text-decoration: none;
      text-transform: none;
      position: absolute;
      right: 0;
      width: 40px;
      padding: 0 5px;
      font-size: 21px;
      height: 37px;
      top: 0;
      text-align: center;
      border-bottom: 1px solid #81acd0;
    }
  }

  &--select-dropdown {
    position: absolute;
    z-index: 2;
    width: 270px;
    max-height: 170px;
    padding: 10px 0;
    margin-top: -17px;
    border: 1px solid rgba(0,0,0,.15);
    box-shadow: 0 6px 12px rgba(0,0,0,.175);
    background-clip: padding-box;
    background-color: #fff;
    overflow: auto;

    @media screen and (min-width: 1350px) {
      max-height: 260px;
    }
  }

  &--select-close {
    position: absolute;
    right: 5px;
    top: 10px;
    cursor: pointer;
    padding: 0;
    background: transparent;
    border: 0;
    width: 20px;
    height: 20px;

    &::before , &::after {
      content: '';
      position: absolute;
      width: 12px;
      height: 2px;
      right: 5px;
      top: 8px;
      background: #005ca9;
      cursor: default;
    }
    &::before {
      transform: rotate(45deg);
    }
    &::after {
      transform: rotate(-45deg);
    }
  }

  &--select-group {
    width: calc(100% - 25px);
  }
  
  &--select-option {
    padding-left: 30px;
    position: relative;
    margin-bottom: 4px;
    cursor: pointer;

    label {
      font-family: "Gotham Book";
      color: #535353;
      cursor: pointer;
      width: 100%;
    }
    input {
      display: none;
    }
    input:checked+label {
      color: #005ca9;

      &::before, &::after {
        content: '';
        position: absolute;
        width: 12px;
        height: 2px;
        left: 10px;
        top: 8px;
        background: #005ca9;
        cursor: default;
      }
      &::before {
        transform: rotate(45deg);
      }
      &::after {
        transform: rotate(-45deg);
      }
    }
  }

  &--hidden {
    @media screen and (min-width: 768px) {
      width: 250px;
    }

    .toggleWidget {
      top: auto;
      right: auto;
      left: 110px;
      right: 50px;

      .figure {
        font-size: 8px;
        top: 4px;
      }
    }

    .interactive-map__widget--header {
      padding: 20px;
    }
    .interactive-map__widget--slider {
      display: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/googleMap/InteractiveMap.vue